import React, { useEffect, useState } from "react";
import { useTheme } from "styled-components";
import {
  FieldName,
  SecurityConfigurationCard,
  ValueAndAction,
  ValueAndActionWrapper,
} from "features/settings/common/styles";
import Title from "common/Typography/Title";
import RouterLink from "common/Buttons/RouterLink";
import {
  CONFIGURATION_DISABLE_TWO_FA_PATH,
  CONFIGURATION_TWO_FA_PATH,
  IS_WEB_VIEW,
} from "common/constants";
import StatusTag from "common/Tags/StatusTag";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import Switch from "common/Buttons/Switch.tsx";
import { UserBiometricsData } from "@shared/services/webview/types.ts";
import {
  requestBiometricDisplayData,
  toggleUserBiometric,
} from "services/webview/webViewBridgeUtils";
import { openMessage } from "utils/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { Check } from "lucide-react";
import SettingsRowLabel from "features/settings/NewLook/SettingsRowLabel.tsx";
import { PasswordRow } from "features/settings/Security/BasicInfo";
import { Pill } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";

interface TwoFAProps {
  /** The new look groups the password row with the other security rows. */
  showPassword?: boolean;
}

const TwoFA = ({ showPassword }: TwoFAProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const appTheme = useTheme();
  const [biometricsData, setBiometricsData] = useState<UserBiometricsData>();

  const getBiometrics = () => {
    if (IS_WEB_VIEW) {
      requestBiometricDisplayData()
        .then((data) => {
          setBiometricsData(data);
        })
        .catch(() => {});
    }
  };

  const onBiometricToggleClick = async () => {
    try {
      const userBiometricsData = await toggleUserBiometric();
      if (
        userBiometricsData?.biometricsEnabled &&
        !userBiometricsData?.hasSavedBiometrics
      )
        openMessage(t("security.biometric_warning"), "warning");
      setBiometricsData({
        ...biometricsData,
        biometricsEnabled: !!userBiometricsData?.biometricsEnabled,
      });
    } catch {}
  };

  useEffect(() => {
    getBiometrics();
  }, []);

  const rows = (
    <>
      {showPassword && <PasswordRow />}
      <ValueAndActionWrapper>
        <FieldName>
          {appTheme.isNewLook ? (
            <SettingsRowLabel>
              {t("security.google_authenticator")}
            </SettingsRowLabel>
          ) : (
            t("security.google_authenticator")
          )}
        </FieldName>
        <ValueAndAction>
          {user?.has_two_factor_auth ? (
            <>
              {appTheme.isNewLook ? (
                <Pill tone="green" icon={Check}>
                  {t("security.active")}
                </Pill>
              ) : (
                <StatusTag
                  color={appTheme.colors.green}
                  text={t("security.active")}
                />
              )}
              <RouterLink to={CONFIGURATION_DISABLE_TWO_FA_PATH}>
                {t("security.deactivate")}
              </RouterLink>
            </>
          ) : (
            <>
              {appTheme.isNewLook ? (
                <Pill tone="red">{t("security.inactive")}</Pill>
              ) : (
                <StatusTag
                  color={appTheme.colors.red}
                  text={t("security.inactive")}
                />
              )}
              <RouterLink to={CONFIGURATION_TWO_FA_PATH}>
                {t("security.activate")}
              </RouterLink>
            </>
          )}
        </ValueAndAction>
      </ValueAndActionWrapper>
      {biometricsData?.biometricsSupported && (
        <ValueAndActionWrapper>
          <FieldName style={{ whiteSpace: "nowrap" }}>
            {appTheme.isNewLook ? (
              <SettingsRowLabel>
                {t("security.biometric_login")}
              </SettingsRowLabel>
            ) : (
              t("security.biometric_login")
            )}
          </FieldName>
          <ValueAndAction style={{ justifyContent: "flex-end" }}>
            <Switch
              checked={biometricsData.biometricsEnabled}
              size="default"
              onChange={onBiometricToggleClick}
            />
          </ValueAndAction>
        </ValueAndActionWrapper>
      )}
    </>
  );

  if (appTheme.isNewLook) {
    return (
      <Panel title={t("security.title")} flush>
        {rows}
      </Panel>
    );
  }

  return (
    <SecurityConfigurationCard>
      <Title level={3}>{t("security.title")}</Title>
      {rows}
    </SecurityConfigurationCard>
  );
};

export default TwoFA;
